import React from 'react';
import { get } from 'lodash';
import { SocialColumn } from '../../columnSelectorDialog/hooks/useColumnDialog';
import { PostTypeInsights } from '@frontend/gql/social/graphql';
import { Row } from '@tanstack/react-table';
import { formatCurrency } from '@frontend/applications/Social/Analytics/hooks/useCurrency';
import MemberPostTypes from '../columnDefs/member-post-types';
import PostsPostTypes from '../columnDefs/posts-post-types';
export const NETWORKS_TABLE_COLUMN_CONFIG_VERSION = 0;

export interface NetworksTableColumn<T = any> extends SocialColumn {
  header: string;
  id: string;
  enableSorting?: boolean;
  enableGlobalFiltering?: boolean;
  hidden: boolean;
  order?: number;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: T) => unknown;
  accessorKey?: string; // accessorKey or accessorFn should be set
  cell: (props: { row: Row<T> }) => React.ReactNode;
  csvValue: (props: { data: T }) => string | null | undefined;
}

export const hydrateColumnsFromJson = (
  jsonColumns: Partial<NetworksTableColumn<PostTypeInsights>>[],
): NetworksTableColumn<PostTypeInsights>[] => {
  const cellMap = Object.fromEntries(defaultNetworksTableColumns.map((col) => [col.id, col.cell]));
  const csvMap = Object.fromEntries(defaultNetworksTableColumns.map((col) => [col.id, col.csvValue]));
  const accessorFnMap = Object.fromEntries(defaultNetworksTableColumns.filter((col) => col.accessorFn).map((col) => [col.id, col.accessorFn]));

  return jsonColumns.map((col) => ({
    ...col,
    cell: col.id && cellMap[col.id] ? cellMap[col.id] : () => null,
    csvValue: col.id && csvMap[col.id] ? csvMap[col.id] : () => '',
    accessorFn: col.id && accessorFnMap[col.id] ? accessorFnMap[col.id] : undefined,
  })) as NetworksTableColumn<PostTypeInsights>[];
};

const POST_TYPE_MAPPING: Record<string, string> = {
  'INSTAGRAM_POST': 'Instagram Post',
  'INSTAGRAM_REEL': 'Instagram Reel',
  'INSTAGRAM_STORY': 'Instagram Story',
  'INSTAGRAM_VIDEO': 'Instagram Video',
  'YOUTUBE_VIDEO': 'YouTube Video',
  'YOUTUBE_SHORT': 'YouTube Short',
  'TIKTOK_VIDEO': 'TikTok Video',
  'PINTEREST_PIN': 'Pinterest Pin',
  'FACEBOOK_POST': 'Facebook Post',
  'TWITTER_POST': 'Twitter Post',
  'BLOG_MENTION': 'Blog Mention',
  'BLOG_DEDICATED': 'Blog Post',
  'SNAPCHAT_STORY': 'Snapchat Story',
  'OTHER': 'Other'
};

export const defaultNetworksTableColumns: NetworksTableColumn<PostTypeInsights>[] = [
  {
    header: 'Post Type',
    id: 'postType',
    accessorKey: 'postType',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 1,
    minSize: 150,
    maxSize: 170,
    csvValue: ({ data }) => {
      const postType = get(data, 'postType');
      return POST_TYPE_MAPPING[postType?.toUpperCase() || ''] || postType;
    },
    cell: ({ row }) => {
      const postType = get(row.original, 'postType');
      const displayValue = POST_TYPE_MAPPING[postType?.toUpperCase() || ''] || postType;
      return <div>{displayValue}</div>;
    },
  },
  {
    header: 'Members',
    id: 'members',
    accessorKey: 'activeMembers',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 2,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'activeMembers');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'activeMembers');
      const postType = get(row.original, 'postType');
      return <MemberPostTypes value={value} postType={postType} />;
    },
  },
  {
    header: 'Posts',
    id: 'posts',
    accessorKey: 'activePosts',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 3,
    minSize: 70,
    maxSize: 90,
    csvValue: ({ data }) => {
      const value = get(data, 'activePosts');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'activePosts');
      const postType = get(row.original, 'postType');
      return <PostsPostTypes value={value} postType={postType} />;
    },
  },
  {
    header: "Posts Created",
    id: "postsCreated",
    accessorKey: "postsCreated",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 4,
    minSize: 130,
    maxSize: 150,
    csvValue: ({ data }) => {
      const value = get(data, 'postsCreated');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postsCreated');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Impressions",
    id: "impressions",
    accessorKey: "impressions",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 5,
    minSize: 115,
    maxSize: 135,
    csvValue: ({ data }) => {
      const value = get(data, 'impressions');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'impressions');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Engagements",
    id: "engagements",
    accessorKey: "engagements",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 6,
    minSize: 130,
    maxSize: 150,
    csvValue: ({ data }) => {
      const value = get(data, 'engagements');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'engagements');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Engagement Rate",
    id: "engagementRate",
    accessorKey: "engagementRate",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 7,
    minSize: 155,
    maxSize: 175,
    csvValue: ({ data }) => {
      const value = get(data, 'engagementRate');
      return value != null ? `${value}%` : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'engagementRate');
      return <div>{value != null ? `${value}%` : ''}</div>;
    },
  },
  {
    header: "Reach",
    id: "reach",
    accessorKey: "reach",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 8,
    minSize: 80,
    maxSize: 100,
    csvValue: ({ data }) => {
      const value = get(data, 'reach');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'reach');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Likes",
    id: "likes",
    accessorKey: "likes",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 9,
    minSize: 75,
    maxSize: 95,
    csvValue: ({ data }) => {
      const value = get(data, 'likes');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'likes');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Comments",
    id: "comments",
    accessorKey: "comments",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 10,
    minSize: 110,
    maxSize: 130, 
    csvValue: ({ data }) => {
      const value = get(data, 'comments');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'comments');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Shares",
    id: "shares",
    accessorKey: "shares",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 11,
    minSize: 80,
    maxSize: 100,
    csvValue: ({ data }) => {
      const value = get(data, 'shares');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'shares');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "Saves",
    id: "saves",
    accessorKey: "saves",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 12,
    minSize: 80,
    maxSize: 100,
    csvValue: ({ data }) => {
      const value = get(data, 'saves');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'saves');
      return <div>{value != null ? value.toLocaleString() : ''}</div>;
    },
  },
  {
    header: "TMV",
    id: "tmv",
    accessorKey: "tmv",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 13,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'tmv');
      const formattedValue = formatCurrency(value);
      return formattedValue;
    },
    cell: ({ row }) => {
      const value = get(row.original, 'tmv');
      return <div>{formatCurrency(value)}</div>;
    },
  }
];
